/**
 * Formatting utility functions for various data types
 */

import { mathUtils } from "./utils.js";

// Status helpers (RUN STATUS / operating points)

const STATUS_TAIL_WORDS = new Set(["edge", "asc", "desc"]);

const STATUS_DISPLAY_MAP = {
  running: "Normal",
  normal: "Normal",
  faulted: "Danger",
  danger: "Danger",
  alerted: "Alerted",
  offline: "Offline",
  shutdown: "Offline",
  nodata: "No Data",
  standby: "Standby",
  unknown: "Unknown",
};

/** Strip trailing "edge", "asc", "desc" from status; keeps other words. */
export function stripStatusTail(status) {
  if (status == null || typeof status !== "string") return "";
  const tokens = status.trim().split(/\s+/).filter(Boolean);
  while (
    tokens.length > 0
    && STATUS_TAIL_WORDS.has(tokens[tokens.length - 1].toLowerCase())
  ) {
    tokens.pop();
  }
  return tokens.join(" ");
}

/** Format stripped status for display: first word via getStatusDisplayText, rest in title case. */
export function formatStatusDisplay(stripped) {
  if (!stripped) return "Unknown";
  const parts = stripped.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "Unknown";
  const first = getStatusDisplayText(parts[0]);
  if (parts.length === 1) return first;
  const rest = parts
    .slice(1)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
  return `${first} ${rest}`;
}

/** Map status (first word) to display label; unknown values get title case. */
export const getStatusDisplayText = (status) => {
  if (status == null || typeof status !== "string") return "Unknown";
  const first = status.trim().split(/\s+/)[0] || "";
  const key = first.toLowerCase();
  return (
    STATUS_DISPLAY_MAP[key]
    || (first ? first.charAt(0).toUpperCase() + first.slice(1) : "Unknown")
  );
};

/**
 * Format duration in human readable format for downtime events
 * @param {number} minutes - Duration in minutes
 * @param {boolean} isOngoing - Whether the duration is ongoing
 * @returns {string} Formatted duration (e.g., "2h 30m", "Ongoing")
 */
export const formatDowntimeDuration = (minutes, isOngoing = false) => {
  if (isOngoing) return "Ongoing";
  if (minutes < 60) return `${mathUtils.round(minutes)}m`;

  const hours = mathUtils.floorDiv(minutes, 60);
  const remainingMinutes = mathUtils.round(minutes % 60);

  if (hours < 24) {
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }

  const days = mathUtils.floorDiv(hours, 24);
  const remainingHours = hours % 24;

  if (days < 7) {
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }

  const weeks = mathUtils.floorDiv(days, 7);
  const remainingDays = days % 7;

  return remainingDays > 0 ? `${weeks}w ${remainingDays}d` : `${weeks}w`;
};

/**
 * Safely apply toFixed to a value with null/undefined handling
 * @param {number|null|undefined} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string|number|null|undefined} Formatted value or original value if not a number
 */
export const toFixedSafe = (value, decimals = 2) => {
  if (value != null && typeof value === "number" && !isNaN(value)) {
    return value.toFixed(decimals);
  }
  return value;
};

/**
 * Format numbers with appropriate precision
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number
 */
export const formatNumber = (value, decimals = 2) => {
  // Parse the value to a number if it's a string
  const numValue =
    typeof value === "string"
      ? parseFloat(removeCommasAndSpaces(value))
      : value;
  if (typeof numValue !== "number" || isNaN(numValue)) return "0";
  // Integer formatting: keep trailing zeros and add thousands separators (e.g. 1000 → "1,000").
  // The decimal trim below would otherwise turn 10 → "1" and 1000 → "1".
  if (decimals === 0) {
    return Math.round(numValue).toLocaleString("en-US");
  }
  const formatted = numValue.toFixed(decimals);
  // Remove trailing zeros and decimal point if all decimals are zero
  return formatted.replace(/\.?0+$/, "");
};

/**
 * Format percentage values
 * @param {number} value - The percentage value (0-100)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 1) => {
  if (typeof value !== "number" || isNaN(value)) return "0%";
  return `${value.toFixed(decimals)}%`;
};

/**
 * Remove commas and spaces from a string value
 * Useful for cleaning numeric strings before parsing
 * @param {string|number} value - The value to clean
 * @returns {string} Cleaned string without commas and spaces
 */
export const removeCommasAndSpaces = (value) => {
  return String(value).replace(/[,\s]/g, "");
};
